import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  updateDoc, 
  query, 
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faClipboardCheck, 
  faQrcode, 
  faList,
  faChevronDown, 
  faChevronUp,
  faChevronLeft,
  faPlus,
  faTimes,
  faSignInAlt,
  faSignOutAlt,
  faSearch,
  faFilter,
  faShare,
  faCheckCircle,
  faTimesCircle,
  faTrash,
  faEdit,
  faChevronRight,
  faPrint,
  faUserShield,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import QrScanner from '../common/QrScanner';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import QRCode from "react-qr-code";
import styles from './TourChecklist.module.css';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';

// Constants
const APP_URL = 'https://ontour.co.in';

// QR Code Modal Component
const QRCodeModal = ({ isOpen, onClose, item, index }) => {
  if (!isOpen || !item) return null;

  const qrCodeData = `checklist_${item.id}_${index}`;
  const qrCodeUrl = `${APP_URL}/scan/tour-checklist/${item.id}_${index}`;

  const handlePrint = () => {
    const printContent = document.getElementById('qr-code-print-area');
    if (printContent) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      iframe.contentDocument.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                text-align: center;
                padding: 20px;
              }
              .qr-container {
                display: inline-block;
                margin: 0 auto;
                padding: 15px;
                border: 1px solid #ddd;
                border-radius: 8px;
              }
              h3 {
                margin-top: 5px;
                margin-bottom: 3px;
                font-size: 24px;
              }
              p {
                margin: 3px 0;
                font-size: 18px;
                color: #E2384D;
                font-weight: 600;
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      
      iframe.contentDocument.close();
      
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.qrCodeModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.qrCodeModalHeader}>
          <h3>QR Code for {item.name} - Item {index + 1}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.qrCodeModalContent} id="qr-code-print-area">
          <div className={styles.qrCodeContainer}>
            <div style={{ background: 'white', padding: '16px', margin: '0 auto' }}>
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={qrCodeData}
                viewBox={`0 0 256 256`}
              />
            </div>
            <h3>{item.name}</h3>
            <p><strong>Item {index + 1}</strong></p>
            {item.model && <p>{item.model}</p>}
            <p>Checklist Item</p>
          </div>
        </div>
        
        <div className={styles.qrCodeModalActions}>
          <button className={styles.printButton} onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} />
            Print QR Code
          </button>
        </div>
      </div>
    </div>
  );
};

// Check Access Modal Component
const CheckAccessModal = ({ isOpen, onClose, eventMembers, allowedUsers, onUpdateAccess }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedUsers(allowedUsers || []);
    }
  }, [isOpen, allowedUsers]);

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(selected => selected.userId === user.userId);
      
      if (isSelected) {
        return prev.filter(selected => selected.userId !== user.userId);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      await onUpdateAccess(selectedUsers);
      onClose();
    } catch (error) {
      console.error('Error updating access:', error);
      alert('Failed to update access permissions');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Manage Check Access</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.modalForm}>
          <p className={styles.accessDescription}>
            Select users who can scan and check items in this checklist:
          </p>
          
          <div className={styles.usersList}>
            {eventMembers.map(member => (
              <div
                key={member.userId}
                className={`${styles.userOption} ${
                  selectedUsers.some(u => u.userId === member.userId) ? styles.selected : ''
                }`}
                onClick={() => toggleUserSelection(member)}
              >
                <img
                  src={member.profilePictureUrl || defaultProfilePhoto}
                  alt={member.fullName}
                  className={styles.memberAvatar}
                />
                <div className={styles.memberInfo}>
                  <span className={styles.memberName}>{member.fullName}</span>
                  <span className={styles.memberDetails}>
                    {member.userType} {member.userSubType && `• ${member.userSubType}`}
                  </span>
                </div>
                {selectedUsers.some(u => u.userId === member.userId) && (
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.selectedIcon} />
                )}
              </div>
            ))}
          </div>
          
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isUpdating}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className={styles.submitButton}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Updating...</span>
                </>
              ) : (
                <span>Update Access</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal for adding/editing checklist items
const ChecklistItemModal = ({ isOpen, onClose, onSave, editingItem = null, eventMembers }) => {
  const [itemName, setItemName] = useState('');
  const [model, setModel] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [description, setDescription] = useState('');
  const [belongsTo, setBelongsTo] = useState([]);
  const [showBelongsToDropdown, setShowBelongsToDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Initialize form with editing item data if provided
  useEffect(() => {
    if (editingItem) {
      setItemName(editingItem.name || '');
      setModel(editingItem.model || '');
      setQuantity(editingItem.quantity || 1);
      setDescription(editingItem.description || '');
      setBelongsTo(editingItem.belongsTo || []);
    } else {
      // Reset form for new item
      setItemName('');
      setModel('');
      setQuantity(1);
      setDescription('');
      setBelongsTo([]);
    }
  }, [editingItem]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!itemName.trim()) {
      alert("Item name is required");
      return;
    }
    
    setIsProcessing(true);
    
    // Ensure all fields have proper values (no undefined)
    const itemData = {
      name: itemName.trim(),
      model: model.trim() || '',
      quantity: parseInt(quantity) || 1,
      description: description.trim() || '',
      belongsTo: belongsTo.map(user => ({
        userId: user.userId || '',
        fullName: user.fullName || '',
        profilePictureUrl: user.profilePictureUrl || '',
        userType: user.userType || '',
        userSubType: user.userSubType || ''
      }))
    };
    
    onSave(itemData, editingItem?.id).finally(() => {
      setIsProcessing(false);
      onClose();
    });
  };
  
  // Toggle user selection in belongsTo array
  const toggleUserSelection = (user) => {
    setBelongsTo(prev => {
      const isSelected = prev.some(selected => selected.userId === user.userId);
      
      if (isSelected) {
        return prev.filter(selected => selected.userId !== user.userId);
      } else {
        return [...prev, user];
      }
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{editingItem ? 'Edit Checklist Item' : 'Add Checklist Item'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label htmlFor="itemName" className={styles.formLabel}>
              Item Name <span className={styles.requiredField}>*</span>
            </label>
            <input
              id="itemName"
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              className={styles.formInput}
              placeholder="Enter item name"
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="model" className={styles.formLabel}>
              Model (Optional)
            </label>
            <input
              id="model"
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className={styles.formInput}
              placeholder="Enter model details"
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="quantity" className={styles.formLabel}>
              Quantity <span className={styles.requiredField}>*</span>
            </label>
            <input
              id="quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className={styles.formInput}
              min="1"
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Belongs To
            </label>
            <div className={styles.belongsToDropdown}>
              <button
                type="button"
                className={styles.dropdownButton}
                onClick={() => setShowBelongsToDropdown(!showBelongsToDropdown)}
              >
                <span>
                  {belongsTo.length 
                    ? `${belongsTo.length} user(s) selected` 
                    : 'Select event members'}
                </span>
                <FontAwesomeIcon 
                  icon={showBelongsToDropdown ? faChevronUp : faChevronDown} 
                />
              </button>
              
              {showBelongsToDropdown && (
                <div className={styles.dropdownContent}>
                  {eventMembers.length > 0 ? (
                    eventMembers.map(member => (
                      <div
                        key={member.userId}
                        className={`${styles.memberOption} ${
                          belongsTo.some(u => u.userId === member.userId) ? styles.selected : ''
                        }`}
                        onClick={() => toggleUserSelection(member)}
                      >
                        <img
                          src={member.profilePictureUrl || defaultProfilePhoto}
                          alt={member.fullName}
                          className={styles.memberAvatar}
                        />
                        <div className={styles.memberInfo}>
                          <span className={styles.memberName}>{member.fullName}</span>
                          <span className={styles.memberDetails}>
                            {member.userType} {member.userSubType && `• ${member.userSubType}`}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.noMembers}>
                      No event members available
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {belongsTo.length > 0 && (
              <div className={styles.selectedMembers}>
                {belongsTo.map(member => (
                  <div key={member.userId} className={styles.selectedMemberTag}>
                    <img
                      src={member.profilePictureUrl || defaultProfilePhoto}
                      alt={member.fullName}
                    />
                    <span>{member.fullName}</span>
                    <button 
                      type="button"
                      className={styles.removeMemberBtn}
                      onClick={() => toggleUserSelection(member)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.formLabel}>
              Description (Max 100 characters)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value.substring(0, 100))}
              className={styles.formTextarea}
              placeholder="Enter short description"
              maxLength={100}
              rows={3}
            />
            <div className={styles.charCounter}>
              {description.length}/100 characters
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{editingItem ? 'Update Item' : 'Add Item'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PDF Modal for Print View
const PdfModal = ({ isOpen, onClose, pdfHtml }) => {
  if (!isOpen) return null;

  const handlePrint = () => {
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(pdfHtml);
    printFrame.contentDocument.close();
    
    setTimeout(() => {
      printFrame.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 100);
    }, 100);
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.pdfModalContent}>
        <div className={styles.pdfModalHeader}>
          <h2>Checklist Preview</h2>
          <div className={styles.pdfModalActions}>
            <button className={styles.printButton} onClick={handlePrint}>
              <FontAwesomeIcon icon={faShare} />
              Print
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        <div className={styles.pdfModalBody}>
          <iframe
            srcDoc={pdfHtml}
            className={styles.pdfIframe}
            title="Checklist Preview"
          />
        </div>
      </div>
    </div>
  );
};

const TourChecklist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tourId, showId, dateId } = useParams();
  const { userData } = useContext(UserContext);
  const { selectedEvent, getSelectedDate, updateDate } = useContext(TourContext);
  
  // Determine event type from URL path or params
  const eventType = location.pathname.includes('/tour/') || tourId ? 'tour' : 'show';
  const eventId = tourId || showId || selectedEvent?.id;
  
  // State for checklist items and UI
  const [checklistItems, setChecklistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checklistMode, setChecklistMode] = useState('checkIn');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // State for event members and access control
  const [eventMembers, setEventMembers] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [showAccessModal, setShowAccessModal] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // QR Scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState('individual');
  const [scanSuccessMessage, setScanSuccessMessage] = useState('');
  
  // QR Code modal state
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedQrItem, setSelectedQrItem] = useState(null);
  const [selectedQrIndex, setSelectedQrIndex] = useState(0);
  
  // PDF Modal state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfHtml, setPdfHtml] = useState('');
  
  // Check if current user is the event creator
  const isCreator = userData?.userId === selectedEvent?.createdBy?.userId;
  
  // Check if user can edit/delete an item
  const canModifyItem = useCallback((item) => {
    return userData?.userId === item.createdBy;
  }, [userData?.userId]);
  
  // Check if user has access to scan/check items
  const hasCheckAccess = useCallback(() => {
    if (!userData?.userId) return false;
    
    // Creator always has access
    if (isCreator) return true;
    
    // Check if user is in allowed users list
    return allowedUsers.some(user => user.userId === userData.userId);
  }, [userData?.userId, isCreator, allowedUsers]);

  // Set up window resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch checklist items and event members when the component mounts
  useEffect(() => {
    if (selectedEvent?.id && dateId && userData?.userId) {
      fetchChecklistItems();
      fetchEventMembers();
      fetchAllowedUsers();
    }
  }, [selectedEvent, dateId, userData]);
  
  // Fetch user data by userId
  const fetchUserData = async (userId) => {
    try {
      const db = getFirestoreInstance();
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          userId: userId,
          fullName: userData.fullName || userData.name || 'Unknown User',
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType || '',
          userSubType: userData.userSubType || ''
        };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching user data for ${userId}:`, error);
      return null;
    }
  };
  
  // Enhanced fetch checklist items with proper belongsTo data
  const fetchChecklistItems = async () => {
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const checklistRef = collection(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklist'
      );
      
      const checklistSnap = await getDocs(checklistRef);
      
      if (checklistSnap.empty) {
        setChecklistItems([]);
        setIsLoading(false);
        return;
      }
      
      const itemsPromises = checklistSnap.docs.map(async (docSnap) => {
        const itemData = docSnap.data();
        
        let belongsToData = [];
        if (itemData.belongsTo && Array.isArray(itemData.belongsTo)) {
          const userPromises = itemData.belongsTo.map(async (userRef) => {
            if (typeof userRef === 'string') {
              return await fetchUserData(userRef);
            } 
            else if (userRef.userId) {
              return await fetchUserData(userRef.userId);
            }
            return userRef;
          });
          
          const resolvedUsers = await Promise.all(userPromises);
          belongsToData = resolvedUsers.filter(Boolean);
        }
        
        // Generate individual items with QR codes
        const individualItems = Array.from({ length: itemData.quantity || 1 }, (_, index) => {
          const itemId = `${docSnap.id}_${index}`;
          const qrCodeUrl = `${APP_URL}/scan/checklist/${itemId}`;
          
          return {
            id: itemId,
            index: index,
            qrCodeUrl: qrCodeUrl,
            checkInStatus: itemData.checkInStatus?.[index] || {
              isCheckedIn: false,
              checkedInBy: null,
              checkedInByName: null,
              checkedInAt: null
            },
            checkOutStatus: itemData.checkOutStatus?.[index] || {
              isCheckedOut: false,
              checkedOutBy: null,
              checkedOutByName: null,
              checkedOutAt: null
            }
          };
        });
        
        return {
          id: docSnap.id,
          ...itemData,
          belongsTo: belongsToData,
          individualItems
        };
      });
      
      const items = await Promise.all(itemsPromises);
      console.log('Processed checklist items with QR codes:', items);
      setChecklistItems(items);
    } catch (error) {
      console.error('Error fetching checklist items:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch event members using userId to get fresh user data
  const fetchEventMembers = async () => {
    try {
      if (!selectedEvent || !eventId) return;
      
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Query assigned users from the assignedUsers subcollection
      const assignedUsersQuery = query(
        collection(db, collectionName, eventId, 'assignedUsers')
      );
      
      const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
      const members = [];
      
      assignedUsersSnapshot.forEach(doc => {
        const userData = doc.data();
        
        // Skip entries that don't have a userId (e.g., bands)
        if (!userData.userId && !doc.id.startsWith('band_')) {
          return;
        }
        
        members.push({
          userId: userData.userId || doc.id,
          fullName: userData.fullName || userData.userName || doc.id,
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.role || userData.userType || 'Member',
          userSubType: userData.userSubType || ''
        });
      });
      
      console.log('Fetched event members from assignedUsers subcollection:', members);
      setEventMembers(members);
    } catch (error) {
      console.error('Error fetching event members:', error);
    }
  };
  
  // Fetch allowed users from checklist metadata
  const fetchAllowedUsers = async () => {
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const metadataRef = doc(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklistMetadata',
        'access'
      );
      
      const metadataDoc = await getDoc(metadataRef);
      
      if (metadataDoc.exists()) {
        const data = metadataDoc.data();
        setAllowedUsers(data.allowedUsers || []);
      } else {
        // Initialize with creator as default
        if (selectedEvent?.createdBy) {
          const creatorData = await fetchUserData(selectedEvent.createdBy.userId);
          if (creatorData) {
            setAllowedUsers([creatorData]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching allowed users:', error);
    }
  };
  
  // Update allowed users
  const updateAllowedUsers = async (users) => {
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const metadataRef = doc(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklistMetadata',
        'access'
      );
      
      await updateDoc(metadataRef, {
        allowedUsers: users,
        updatedAt: serverTimestamp(),
        updatedBy: userData.userId
      });
      
      setAllowedUsers(users);
    } catch (error) {
      console.error('Error updating allowed users:', error);
      throw error;
    }
  };
  
  // Handle back button
  const handleBack = () => {
    if (location.state?.fromEvent) {
      navigate(`/${eventType}/${eventId}/${dateId}`, {
        state: { activeTab: 'event' }
      });
    } else {
      const prevPath = location.state?.previousPath;
      if (prevPath) {
        navigate(prevPath);
      } else {
        navigate(`/${eventType}/${eventId}/${dateId}`);
      }
    }
  };
  
  // Toggle item details expansion
  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  // Handle inventory search
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    const searchTermLower = searchTerm.toLowerCase();
    
    const results = checklistItems.filter(item => {
      const nameMatch = item.name?.toLowerCase().includes(searchTermLower);
      const modelMatch = item.model?.toLowerCase().includes(searchTermLower);
      const descriptionMatch = item.description?.toLowerCase().includes(searchTermLower);
      const belongsToMatch = item.belongsTo?.some(user => 
        user.fullName?.toLowerCase().includes(searchTermLower)
      );
      
      return nameMatch || modelMatch || descriptionMatch || belongsToMatch;
    });
    
    setSearchResults(results);
  };
  
  // Get filtered items based on search
  const getFilteredItems = () => {
    if (isSearching) {
      return searchResults;
    }
    return checklistItems;
  };
  
  // Add or edit checklist item with QR code generation
  const handleSaveItem = async (itemData, itemId = null) => {
    try {
      const db = getFirestoreInstance();
      const timestamp = Timestamp.now();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const checklistRef = collection(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklist'
      );
      
      // Generate QR codes for individual items
      const individualItemsData = Array.from({ length: itemData.quantity }, (_, index) => {
        const uniqueItemId = itemId ? `${itemId}_${index}` : `${Date.now()}_${index}`;
        return {
          id: uniqueItemId,
          index: index,
          qrCodeUrl: `${APP_URL}/scan/checklist/${uniqueItemId}`
        };
      });
      
      const cleanItemData = {
        name: itemData.name || '',
        model: itemData.model || '',
        quantity: itemData.quantity || 1,
        description: itemData.description || '',
        belongsTo: itemData.belongsTo || [],
        individualItemsData,
        checkInStatus: [],
        checkOutStatus: [],
        createdBy: userData.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: userData.userId
      };
      
      if (itemId) {
        const itemDocRef = doc(checklistRef, itemId);
        
        const existingItem = await getDoc(itemDocRef);
        if (existingItem.exists()) {
          const existingData = existingItem.data();
          
          await updateDoc(itemDocRef, {
            ...cleanItemData,
            checkInStatus: existingData.checkInStatus || [],
            checkOutStatus: existingData.checkOutStatus || []
          });
        } else {
          await addDoc(checklistRef, cleanItemData);
        }
      } else {
        await addDoc(checklistRef, cleanItemData);
      }
      
      fetchChecklistItems();
      
    } catch (error) {
      console.error('Error saving checklist item:', error);
      throw error;
    }
  };
  
  // Delete a checklist item (only if user created it)
  const handleDeleteItem = async (itemId) => {
    const item = checklistItems.find(i => i.id === itemId);
    if (!item || !canModifyItem(item)) {
      alert('You can only delete items that you created.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const itemRef = doc(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklist',
        itemId
      );
      
      await updateDoc(itemRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: userData.userId
      });
      
      setChecklistItems(prevItems => 
        prevItems.filter(item => item.id !== itemId)
      );
      
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };
  
  // Mark an individual item as checked in/out
  const handleMarkItem = async (itemId, index, action) => {
    if (!hasCheckAccess()) {
      alert('You do not have permission to check items. Contact the event creator for access.');
      return false;
    }
    
    try {
      const db = getFirestoreInstance();
      const timestamp = Timestamp.now();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const item = checklistItems.find(item => item.id === itemId);
      if (!item) return false;
      
      const individualItem = item.individualItems[index];
      if ((action === 'checkIn' && individualItem.checkInStatus?.isCheckedIn) || 
          (action === 'checkOut' && individualItem.checkOutStatus?.isCheckedOut)) {
        return false;
      }
      
      const itemRef = doc(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklist',
        itemId
      );
      
      const itemDoc = await getDoc(itemRef);
      if (!itemDoc.exists()) return false;
      
      const itemData = itemDoc.data();
      
      let updatedStatus = itemData[action === 'checkIn' ? 'checkInStatus' : 'checkOutStatus'] || [];
      
      while (updatedStatus.length < item.quantity) {
        updatedStatus.push({
          isCheckedIn: false,
          checkedInBy: null,
          checkedInByName: null,
          checkedInAt: null
        });
      }
      
      updatedStatus[index] = action === 'checkIn' 
        ? {
            isCheckedIn: true,
            checkedInBy: userData.userId,
            checkedInByName: userData.fullName || 'Unknown User',
            checkedInAt: timestamp
          }
        : {
            isCheckedOut: true,
            checkedOutBy: userData.userId,
            checkedOutByName: userData.fullName || 'Unknown User',
            checkedOutAt: timestamp
          };
      
      await updateDoc(itemRef, {
        [action === 'checkIn' ? 'checkInStatus' : 'checkOutStatus']: updatedStatus,
        updatedAt: serverTimestamp(),
        updatedBy: userData.userId
      });
      
      setChecklistItems(prevItems => 
        prevItems.map(item => {
          if (item.id !== itemId) return item;
          
          const updatedIndividualItems = [...item.individualItems];
          updatedIndividualItems[index] = {
            ...updatedIndividualItems[index],
            [action === 'checkIn' ? 'checkInStatus' : 'checkOutStatus']: {
              [action === 'checkIn' ? 'isCheckedIn' : 'isCheckedOut']: true,
              [action === 'checkIn' ? 'checkedInBy' : 'checkedOutBy']: userData.userId,
              [action === 'checkIn' ? 'checkedInByName' : 'checkedOutByName']: userData.fullName || 'Unknown User',
              [action === 'checkIn' ? 'checkedInAt' : 'checkedOutAt']: timestamp
            }
          };
          
          return {
            ...item,
            individualItems: updatedIndividualItems
          };
        })
      );
      
      setScanSuccessMessage(`Successfully ${action === 'checkIn' ? 'checked in' : 'checked out'} ${item.name} (${index + 1})`);
      
      setTimeout(() => {
        setScanSuccessMessage('');
      }, 3000);
      
      return true;
    } catch (error) {
      console.error(`Error marking item as ${action === 'checkIn' ? 'checked in' : 'checked out'}:`, error);
      return false;
    }
  };
  
  // Mark all items in a group
  const handleMarkAllItems = async (itemId, action) => {
    if (!hasCheckAccess()) {
      alert('You do not have permission to check items. Contact the event creator for access.');
      return false;
    }
    
    try {
      const db = getFirestoreInstance();
      const timestamp = Timestamp.now();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      const item = checklistItems.find(item => item.id === itemId);
      if (!item) return false;
      
      const itemRef = doc(
        db,
        collectionName,
        eventId,
        'eventDates',
        dateId,
        'checklist',
        itemId
      );
      
      const itemDoc = await getDoc(itemRef);
      if (!itemDoc.exists()) return false;
      
      const updatedStatus = Array.from({ length: item.quantity }, () => ({
        [action === 'checkIn' ? 'isCheckedIn' : 'isCheckedOut']: true,
        [action === 'checkIn' ? 'checkedInBy' : 'checkedOutBy']: userData.userId,
        [action === 'checkIn' ? 'checkedInByName' : 'checkedOutByName']: userData.fullName || 'Unknown User',
        [action === 'checkIn' ? 'checkedInAt' : 'checkedOutAt']: timestamp
      }));
      
      await updateDoc(itemRef, {
        [action === 'checkIn' ? 'checkInStatus' : 'checkOutStatus']: updatedStatus,
        updatedAt: serverTimestamp(),
        updatedBy: userData.userId
      });
      
      setChecklistItems(prevItems => 
        prevItems.map(item => {
          if (item.id !== itemId) return item;
          
          const updatedIndividualItems = Array.from({ length: item.quantity }, (_, index) => ({
            id: `${itemId}_${index}`,
            index: index,
            qrCodeUrl: `${APP_URL}/scan/checklist/${itemId}_${index}`,
            [action === 'checkIn' ? 'checkInStatus' : 'checkOutStatus']: {
              [action === 'checkIn' ? 'isCheckedIn' : 'isCheckedOut']: true,
              [action === 'checkIn' ? 'checkedInBy' : 'checkedOutBy']: userData.userId,
              [action === 'checkIn' ? 'checkedInByName' : 'checkedOutByName']: userData.fullName || 'Unknown User',
              [action === 'checkIn' ? 'checkedInAt' : 'checkedOutAt']: timestamp
            }
          }));
          
          return {
            ...item,
            individualItems: updatedIndividualItems
          };
        })
      );
      
      setScanSuccessMessage(`Successfully ${action === 'checkIn' ? 'checked in' : 'checked out'} all ${item.name} items`);
      
      setTimeout(() => {
        setScanSuccessMessage('');
      }, 3000);
      
      return true;
    } catch (error) {
      console.error(`Error marking all items as ${action === 'checkIn' ? 'checked in' : 'checked out'}:`, error);
      return false;
    }
  };
  
  // Handle QR code scanning
  const handleQrScan = async (data, setSuccessMessage, setErrorMessage) => {
    if (data === 'CHANGE_MODE_INDIVIDUAL') {
      setScannerMode('individual');
      return;
    }
    
    if (data === 'CHANGE_MODE_MARKALL') {
      setScannerMode('markAll');
      return;
    }
    
    try {
      let itemId, index;
      
      if (data.startsWith('checklist_')) {
        const parts = data.split('_');
        if (parts.length >= 3) {
          itemId = parts[1];
          index = parseInt(parts[2]);
        }
      } else {
        setErrorMessage('Invalid QR code format. Please try again.');
        return;
      }
      
      if (scannerMode === 'individual') {
        if (isNaN(index)) {
          setErrorMessage('Invalid item index in QR code.');
          return;
        }
        
        const item = checklistItems.find(item => item.id === itemId);
        if (!item) {
          setErrorMessage('Item not found in checklist.');
          return;
        }
        
        if (index < 0 || index >= item.quantity) {
          setErrorMessage('Invalid item index.');
          return;
        }
        
        const individualItem = item.individualItems[index];
        if ((checklistMode === 'checkIn' && individualItem.checkInStatus?.isCheckedIn) ||
            (checklistMode === 'checkOut' && individualItem.checkOutStatus?.isCheckedOut)) {
          setErrorMessage(`This item is already ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'}.`);
          return;
        }
        
        const success = await handleMarkItem(itemId, index, checklistMode);
        
        if (success) {
          setSuccessMessage(`Successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'} ${item.name} (${index + 1})`);
        } else {
          setErrorMessage('Failed to update item status. Please try again.');
        }
      } else {
        const item = checklistItems.find(item => item.id === itemId);
        if (!item) {
          setErrorMessage('Item not found in checklist.');
          return;
        }
        
        const success = await handleMarkAllItems(itemId, checklistMode);
        
        if (success) {
          setSuccessMessage(`Successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'} all ${item.name} items`);
        } else {
          setErrorMessage('Failed to update items. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setErrorMessage(`Error: ${error.message}`);
    }
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    try {
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp.seconds && timestamp.nanoseconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return 'N/A';
    }
  };
  
  // Get the latest checked timestamp for an item
  const getLatestTimestamp = (item, mode) => {
    if (!item.individualItems) return 'Not checked yet';
    
    const checkedItems = item.individualItems
      .filter(individualItem => 
        mode === 'checkIn' 
          ? individualItem.checkInStatus?.isCheckedIn 
          : individualItem.checkOutStatus?.isCheckedOut
      )
      .map(individualItem => 
        mode === 'checkIn' 
          ? individualItem.checkInStatus?.checkedInAt 
          : individualItem.checkOutStatus?.checkedOutAt
      )
      .filter(Boolean);
    
    if (checkedItems.length === 0) return 'Not checked yet';
    
    const latestTimestamp = checkedItems.reduce((latest, current) => {
      const currentTime = current?.seconds ? current.seconds * 1000 : new Date(current).getTime();
      const latestTime = latest?.seconds ? latest.seconds * 1000 : new Date(latest).getTime();
      return currentTime > latestTime ? current : latest;
    });
    
    return formatTimestamp(latestTimestamp);
  };
  
  // Get who checked the items (single user or "Various Users")
  const getCheckedByUser = (item, mode) => {
    if (!item.individualItems) return 'N/A';
    
    const checkedByUsers = item.individualItems
      .filter(individualItem => 
        mode === 'checkIn' 
          ? individualItem.checkInStatus?.isCheckedIn 
          : individualItem.checkOutStatus?.isCheckedOut
      )
      .map(individualItem => 
        mode === 'checkIn' 
          ? individualItem.checkInStatus?.checkedInByName 
          : individualItem.checkOutStatus?.checkedOutByName
      )
      .filter(Boolean);
    
    if (checkedByUsers.length === 0) return 'N/A';
    
    const uniqueUsers = [...new Set(checkedByUsers)];
    
    if (uniqueUsers.length === 1) {
      return uniqueUsers[0];
    } else {
      return 'Various Users';
    }
  };
  
  // Generate HTML for PDF export
  const generateChecklistHtml = () => {
    const eventDate = getSelectedDate();
    const mode = checklistMode === 'checkIn' ? 'Check In' : 'Check Out';
    const items = getFilteredItems();
    
    const rows = items
      .map((item) => {
        const itemRows = [];
        
        // Format belongs to users for display
        const belongsToText = item.belongsTo && item.belongsTo.length > 0 
          ? item.belongsTo.map(user => user.fullName).join(', ')
          : 'N/A';
        
        itemRows.push(`
          <tr class="group-row">
            <td colspan="6" style="background-color: #f8f9fa; font-weight: bold; text-align: left;">
              ${item.name || 'Unnamed Item'} ${item.model ? `(${item.model})` : ''}
            </td>
          </tr>
        `);
        
        if (item.individualItems && item.individualItems.length > 0) {
          item.individualItems.forEach((individualItem, index) => {
            const isChecked = checklistMode === 'checkIn' 
              ? (individualItem.checkInStatus?.isCheckedIn || false)
              : (individualItem.checkOutStatus?.isCheckedOut || false);
            
            const timestamp = checklistMode === 'checkIn'
              ? (individualItem.checkInStatus?.checkedInAt ? formatTimestamp(individualItem.checkInStatus.checkedInAt) : 'N/A')
              : (individualItem.checkOutStatus?.checkedOutAt ? formatTimestamp(individualItem.checkOutStatus.checkedOutAt) : 'N/A');
            
            const userName = checklistMode === 'checkIn'
              ? (individualItem.checkInStatus?.checkedInByName || 'N/A')
              : (individualItem.checkOutStatus?.checkedOutByName || 'N/A');
            
            itemRows.push(`
              <tr>
                <td style="padding-left: 30px;">${item.name} ${index + 1}</td>
                <td>${belongsToText}</td>
                <td>${isChecked ? '✓' : ''}</td>
                <td>${timestamp}</td>
                <td>${userName}</td>
                <td></td>
              </tr>
            `);
          });
        }
        
        return itemRows.join('');
      })
      .join('');
    
    const venueInfo = eventDate?.event?.venueDetails?.venueData;
    const eventName = selectedEvent?.type === 'show' ? 
      (selectedEvent.showName || selectedEvent.tourName) : 
      selectedEvent.tourName;
    
    return `
      <html>
        <head>
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            h1, h2, h3, h4 {
              text-align: center;
              margin: 10px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
              word-wrap: break-word;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
              text-align: center;
            }
            .group-row td {
              background-color: #f8f9fa;
            }
            .summary {
              margin-top: 20px;
              border: 1px solid #ddd;
              padding: 10px;
              background-color: #f9f9f9;
            }
            .check-mark {
              color: green;
              font-weight: bold;
            }
            /* Adjust column widths for better fit */
            th:nth-child(1) { width: 25%; } /* Item */
            th:nth-child(2) { width: 20%; } /* Belongs To */
            th:nth-child(3) { width: 10%; } /* Check status */
            th:nth-child(4) { width: 20%; } /* Timestamp */
            th:nth-child(5) { width: 15%; } /* Checked by */
            th:nth-child(6) { width: 10%; } /* Notes */
          </style>
        </head>
        <body>
          <h1>${eventName || 'Event Inventory'}</h1>
          <h2>${selectedEvent?.actDetails?.name || ''}</h2>
          <h3>${new Date(eventDate?.date).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}</h3>
          <h4>${venueInfo?.displayName || ''} ${venueInfo?.formattedAddress || ''}</h4>
  
          <h2>${mode} Checklist</h2>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Belongs To</th>
                <th>${mode}</th>
                <th>${checklistMode === 'checkIn' ? 'Checked In At' : 'Checked Out At'}</th>
                <th>${checklistMode === 'checkIn' ? 'Checked In By' : 'Checked Out By'}</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          <div class="summary">
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Generated By:</strong> ${userData?.fullName || 'User'}</p>
          </div>
        </body>
      </html>
    `;
  };
  
  // Handle export checklist to PDF
  const handleExportChecklist = async () => {
    try {
      const html = generateChecklistHtml();
      setPdfHtml(html);
      setShowPdfModal(true);
    } catch (error) {
      console.error('Error exporting checklist:', error);
      alert('Failed to export checklist. Please try again.');
    }
  };
  
  // Calculate the number of checked items for an item
  const getCheckedCount = (item, mode) => {
    if (!item.individualItems) return 0;
    
    return item.individualItems.filter(individualItem => 
      mode === 'checkIn' 
        ? individualItem.checkInStatus?.isCheckedIn 
        : individualItem.checkOutStatus?.isCheckedOut
    ).length;
  };

  // Handle QR code button click
  const handleQrCodeClick = (item, index) => {
    setSelectedQrItem(item);
    setSelectedQrIndex(index);
    setShowQrModal(true);
  };
  
  // Render the checklist table UI
  const renderChecklistTable = () => {
    const filteredItems = getFilteredItems();
    
    return (
      <div className={styles.checklistTable}>
        {isMobile ? (
          <div className={styles.mobileTableHeader}>
            <div className={styles.itemColumn}>Item</div>
            <div className={styles.qtyColumn}>Qty</div>
            <div className={styles.statusColumn}>
              {checklistMode === 'checkIn' ? 'In' : 'Out'}
            </div>
            <div className={styles.actionsColumn}>Actions</div>
          </div>
        ) : (
          <div className={styles.tableHeader}>
            <div className={styles.itemColumn}>Item</div>
            <div className={styles.qtyColumn}>Qty</div>
            <div className={styles.statusColumn}>
              {checklistMode === 'checkIn' ? 'Check In' : 'Check Out'}
            </div>
            <div className={styles.timeColumn}>
              {checklistMode === 'checkIn' ? 'Latest Check In' : 'Latest Check Out'}
            </div>
            <div className={styles.byColumn}>
              {checklistMode === 'checkIn' ? 'Checked In By' : 'Checked Out By'}
            </div>
            <div className={styles.actionsColumn}>Actions</div>
          </div>
        )}
        
        <div className={styles.tableBody}>
          {filteredItems.map((item) => (
            <React.Fragment key={item.id}>
              <div 
                className={styles.groupRow}
                onClick={() => toggleItemExpansion(item.id)}
              >
                <div className={styles.itemColumn}>
                  <FontAwesomeIcon 
                    icon={expandedItems[item.id] ? faChevronDown : faChevronRight} 
                    className={styles.expandIcon}
                  />
                  <span>
                    {item.name}
                    {item.model && <span className={styles.modelText}> ({item.model})</span>}
                  </span>
                </div>
                <div className={styles.qtyColumn}>
                  {item.quantity}
                </div>
                <div className={styles.statusColumn}>
                  <label className={styles.markAllLabel}>
                    <input
                      type="checkbox"
                      checked={getCheckedCount(item, checklistMode) === item.quantity}
                      onChange={() => handleMarkAllItems(item.id, checklistMode)}
                      disabled={getCheckedCount(item, checklistMode) === item.quantity || !hasCheckAccess()}
                    />
                    <span>All</span>
                  </label>
                </div>
                {!isMobile && (
                  <>
                    <div className={styles.timeColumn}>
                      {getLatestTimestamp(item, checklistMode)}
                    </div>
                    <div className={styles.byColumn}>
                      {getCheckedByUser(item, checklistMode)}
                    </div>
                  </>
                )}
                <div className={styles.actionsColumn}>
                  {canModifyItem(item) && (
                    <>
                      <button 
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingItem(item);
                          setIsModalOpen(true);
                        }}
                        title="Edit item"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteItem(item.id);
                        }}
                        title="Delete item"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {expandedItems[item.id] && (
                <div className={styles.expandedSection}>
                  {item.description && (
                    <div className={styles.itemDescription}>
                      <strong>Description:</strong> {item.description}
                    </div>
                  )}
                  
                  {item.belongsTo && item.belongsTo.length > 0 && (
                    <div className={styles.itemBelongsTo}>
                      <strong>Belongs to:</strong>
                      <div className={styles.belongsToTags}>
                        {item.belongsTo.map(user => (
                          <div key={user.userId} className={styles.belongsToTag}>
                            <img 
                              src={user.profilePictureUrl || defaultProfilePhoto} 
                              alt={user.fullName}
                            />
                            <span>{user.fullName}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {Array.from({ length: item.quantity }).map((_, index) => {
                    const individualItem = item.individualItems[index];
                    
                    return (
                      <div key={index} className={styles.individualItem}>
                        <div className={styles.itemColumn}>
                          <span className={styles.itemIndex}>{index + 1}</span>
                          {item.name}
                        </div>
                        <div className={styles.checkboxColumn}>
                          <input
                            type="checkbox"
                            checked={
                              checklistMode === 'checkIn' 
                                ? (individualItem?.checkInStatus?.isCheckedIn || false)
                                : (individualItem?.checkOutStatus?.isCheckedOut || false)
                            }
                            onChange={() => handleMarkItem(item.id, index, checklistMode)}
                            disabled={
                              (checklistMode === 'checkIn' 
                                ? (individualItem?.checkInStatus?.isCheckedIn || false)
                                : (individualItem?.checkOutStatus?.isCheckedOut || false)) ||
                              !hasCheckAccess()
                            }
                          />
                        </div>
                        <div className={styles.timestampColumn}>
                          {checklistMode === 'checkIn' 
                            ? (individualItem?.checkInStatus?.checkedInAt 
                                ? formatTimestamp(individualItem.checkInStatus.checkedInAt) 
                                : 'Not checked in')
                            : (individualItem?.checkOutStatus?.checkedOutAt 
                                ? formatTimestamp(individualItem.checkOutStatus.checkedOutAt) 
                                : 'Not checked out')
                          }
                        </div>
                        <div className={styles.userColumn}>
                          {checklistMode === 'checkIn' 
                            ? (individualItem?.checkInStatus?.checkedInByName || 'N/A')
                            : (individualItem?.checkOutStatus?.checkedOutByName || 'N/A')
                          }
                        </div>
                        <div className={styles.qrCodeColumn}>
                          <button
                            className={styles.qrButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQrCodeClick(item, index);
                            }}
                            title="View QR Code"
                          >
                            <FontAwesomeIcon icon={faQrcode} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Format date helper function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  if (!selectedEvent || !dateId) {
    return (
      <div className={styles.layout}>
        <TopBar />
        <div className={styles.mainLayout}>
          {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
          <main className={styles.content}>
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faClipboardCheck} size="2x" />
              <p>Please select an event and date to view checklist</p>
            </div>
          </main>
          {!isMobile && (
            <RightMenu
              selectedEvent={selectedEvent}
              events={[]}
              onEventSelect={() => {}}
            />
          )}
        </div>
      </div>
    );
  }

  const eventName = selectedEvent?.type === 'show' ? 
    (selectedEvent.showName || selectedEvent.tourName) : 
    selectedEvent.tourName;

  return (
    <div className={styles.layout}>
      <TopBar />
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        <main className={styles.content}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
              Back to Schedule
            </button>
            <h1>{eventName}</h1>
            <h2>{formatDate(getSelectedDate()?.date)}</h2>
          </div>
          
          <div className={styles.checklistSection}>
            <div className={styles.checklistHeader}>
              <div className={styles.checklistActions}>
                <button 
                  className={`${styles.modeButton} ${checklistMode === 'checkIn' ? styles.activeMode : ''}`}
                  onClick={() => setChecklistMode('checkIn')}
                >
                  <FontAwesomeIcon icon={faSignInAlt} />
                  <span>Check-In Mode</span>
                </button>
                <button 
                  className={`${styles.modeButton} ${checklistMode === 'checkOut' ? styles.activeMode : ''}`}
                  onClick={() => setChecklistMode('checkOut')}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Check-Out Mode</span>
                </button>
              </div>
              
              <div className={styles.headerActions}>
                <button 
                  className={styles.scanButton}
                  onClick={() => setShowScanner(true)}
                  disabled={!hasCheckAccess()}
                  title={!hasCheckAccess() ? "You don't have permission to scan items" : "Scan By QR Code"}
                >
                  <FontAwesomeIcon icon={faQrcode} />
                  <span>Scan By QR Code</span>
                </button>
                
                <button 
                  className={styles.printButton}
                  onClick={handleExportChecklist}
                >
                  <FontAwesomeIcon icon={faShare} />
                  <span>Print Checklist</span>
                </button>
              </div>
            </div>
            
            {scanSuccessMessage && (
              <div className={styles.successMessage}>
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{scanSuccessMessage}</span>
              </div>
            )}
            
            <div className={styles.searchContainer}>
              <div className={styles.searchBar}>
                <input 
                  type="text" 
                  placeholder="Search items..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className={styles.searchInput}
                />
                <button 
                  className={styles.searchButton}
                  onClick={handleSearch}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
              
              <div className={styles.buttonGroup}>
                <button 
                  className={styles.addButton}
                  onClick={() => {
                    setEditingItem(null);
                    setIsModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add Item</span>
                </button>
                
                {isCreator && (
                  <button 
                    className={styles.accessButton}
                    onClick={() => setShowAccessModal(true)}
                  >
                    <FontAwesomeIcon icon={faUserShield} />
                    <span>Check Access</span>
                  </button>
                )}
              </div>
            </div>
            
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Loading checklist items...</p>
              </div>
            ) : getFilteredItems().length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faClipboardCheck} className={styles.emptyIcon} />
                <p className={styles.emptyText}>
                  {isSearching 
                    ? `No items found matching "${searchTerm}"`
                    : "No checklist items yet. Click 'Add Item' to create one."}
                </p>
              </div>
            ) : (
              renderChecklistTable()
            )}
          </div>
          
          {/* Modals */}
          <ChecklistItemModal 
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingItem(null);
            }}
            onSave={handleSaveItem}
            editingItem={editingItem}
            eventMembers={eventMembers}
          />
          
          <CheckAccessModal
            isOpen={showAccessModal}
            onClose={() => setShowAccessModal(false)}
            eventMembers={eventMembers}
            allowedUsers={allowedUsers}
            onUpdateAccess={updateAllowedUsers}
          />
          
          <QRCodeModal
            isOpen={showQrModal}
            onClose={() => setShowQrModal(false)}
            item={selectedQrItem}
            index={selectedQrIndex}
          />
          
          <QrScanner 
            isOpen={showScanner}
            onClose={() => setShowScanner(false)}
            onScan={handleQrScan}
            mode={scannerMode}
            checklistMode={checklistMode}
            keepOpenAfterScan={true}
          />
          
          {showPdfModal && (
            <PdfModal
              isOpen={showPdfModal}
              onClose={() => setShowPdfModal(false)}
              pdfHtml={pdfHtml}
            />
          )}
        </main>
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={[]}
            onTourSelect={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default TourChecklist;